import { useState } from 'react';
import { Card, Button, Image, Tag, Modal, message } from 'antd';
import { CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { Search } from 'lucide-react';
import { unbindOmsRecycleOrderAuthenticationBag } from 'apis/oms';
import AuthenticationDetailModal, {
  getAuthenticationDetailVerdictStyle,
} from '../recycling-consignment-order-v2/product-list/form-items/authentication-detail-modal';
import {
  getAuthenticationDisplaySummary,
  getAuthenticationDisplayVerdictText,
} from '../recycling-consignment-order-v2/product-list/form-items/authentication-display';
import type { AuthenticationDetailData } from './useHandleDetailData';
import i18n from '../../../i18n';
import LOCALS from '../../../commons/locals';

interface AuthenticationResultCardProps {
  data: AuthenticationDetailData | null;
  omsRecycleOrderId?: string;
  onUnbindSuccess?: () => void | Promise<void>;
}

const AuthenticationResultCard = ({
  data,
  omsRecycleOrderId,
  onUnbindSuccess,
}: AuthenticationResultCardProps) => {
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [unbindLoading, setUnbindLoading] = useState(false);

  if (!data) return null;

  const { bagInfo, images, latestTask, latestResult, detectionPoints } = data;

  // 获取第一张图片
  const firstImage = images?.[0]?.publicUrl;

  const displaySummary = getAuthenticationDisplaySummary({
    latestResult,
    detectionPoints,
  });
  const verdictText = getAuthenticationDisplayVerdictText(
    displaySummary,
    i18n.t.bind(i18n),
  );
  const verdictStyle =
    displaySummary.mode === 'normal'
      ? getAuthenticationDetailVerdictStyle(
          displaySummary.displayVerdictKind,
          displaySummary.displayScore,
        )
      : undefined;
  const isWarningVerdict = displaySummary.mode !== 'normal';

  // 鉴定编号（优先使用 certificateNo）
  const certificateNo =
    latestTask?.certificateNo ||
    latestResult?.authenticationId ||
    bagInfo?.bagId;

  // authenticationBagId 用于查询详情
  const authenticationBagId = bagInfo?.id;

  const handleUnbind = () => {
    if (!omsRecycleOrderId) return;

    Modal.confirm({
      title: i18n.t(LOCALS.unbind_authentication_confirm_title),
      content: i18n.t(LOCALS.unbind_authentication_confirm_content),
      okButtonProps: {
        danger: true,
      },
      onOk: async () => {
        setUnbindLoading(true);
        try {
          const res = await unbindOmsRecycleOrderAuthenticationBag({
            omsRecycleOrderId,
          });
          message.success(
            res?.message || String(i18n.t(LOCALS.unbind_authentication_success)),
          );
          await onUnbindSuccess?.();
        } catch (error) {
          message.error(String(i18n.t(LOCALS.unbind_authentication_failed)));
          throw error;
        } finally {
          setUnbindLoading(false);
        }
      },
    });
  };

  return (
    <>
      <Card bodyStyle={{ padding: '20px 24px' }}>
        {/* 标题栏 */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-base font-medium">
              <Search className="w-5 h-5 text-gray-600" />
              <span>{i18n.t(LOCALS.bag_authentication)}</span>
            </div>
            {certificateNo && (
              <span className="text-gray-500 text-sm">
                {i18n.t(LOCALS.authentication_no)}：{certificateNo}
              </span>
            )}
          </div>
        </div>

        {/* 内容区域 */}
        <div className="flex items-center divide-x divide-gray-200">
          {/* 左侧图片 */}
          {firstImage && (
            <div className="flex-shrink-0 pr-6">
              <Image
                src={firstImage}
                alt={String(i18n.t(LOCALS.authentication_product))}
                width={120}
                height={120}
                style={{ objectFit: 'cover', borderRadius: 6 }}
              />
            </div>
          )}

          {/* 鉴定结论 */}
          <div className="px-6 flex flex-col items-center gap-2 min-w-[140px]">
            <span className="text-gray-500 text-sm">
              {i18n.t(LOCALS.authentication_conclusion)}
            </span>
            {verdictText ? (
              isWarningVerdict ? (
                <Tag
                  icon={<WarningOutlined />}
                  className="m-0 rounded-full px-3 py-1 text-sm font-medium border-0"
                  style={{ backgroundColor: '#FFF9C4', color: '#F57F17' }}
                >
                  {verdictText}
                </Tag>
              ) : (
                <Tag
                  icon={<CheckCircleOutlined />}
                  className="m-0 rounded-full px-3 py-1 text-sm font-medium border-0"
                  style={verdictStyle}
                >
                  {verdictText}
                </Tag>
              )
            ) : (
              <span className="text-gray-400">-</span>
            )}
          </div>

          <div className="px-6 flex flex-col gap-2 min-w-[240px]">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-blue-50 text-blue-600 text-xs font-semibold flex items-center justify-center">
                AI
              </span>
              <span className="text-gray-500 text-sm">
                {i18n.t(LOCALS.ai_authentication)}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-700">
              {verdictText && (
                <Tag
                  className="m-0 rounded-full px-2 py-0.5 text-xs border-0"
                  style={
                    isWarningVerdict
                      ? { backgroundColor: '#FFF9C4', color: '#F57F17' }
                      : verdictStyle
                  }
                >
                  {verdictText}
                </Tag>
              )}
            </div>
          </div>
        </div>

        {authenticationBagId && (
          <div className="flex justify-end mt-5">
            <div className="flex flex-col items-end gap-3">
              {omsRecycleOrderId && (
                <Button danger loading={unbindLoading} onClick={handleUnbind}>
                  {i18n.t(LOCALS.unbind_authentication_task)}
                </Button>
              )}
              <Button
                type="primary"
                className="h-9 px-6 rounded-md"
                onClick={() => setDetailModalOpen(true)}
              >
                {i18n.t(LOCALS.view_details)}
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* 鉴定详情弹窗 */}
      <AuthenticationDetailModal
        open={detailModalOpen}
        authenticationBagId={authenticationBagId}
        onClose={() => setDetailModalOpen(false)}
      />
    </>
  );
};

export default AuthenticationResultCard;
